// src/components/SendListingsDialog.tsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  useMediaQuery,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Chip,
  alpha,
  useTheme,
  Avatar,
  Collapse,
  Tooltip,
  InputAdornment,
  FormControlLabel,
  Switch,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Visibility as PreviewIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AutoFixHigh as AutoFixHighIcon,
  Subject as SubjectIcon,
  Message as MessageIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { supabase } from "../lib/supabase";
import { fetchWithAuth } from "../lib/api";
import {
  emailService,
  generateListingEmailHtml,
  groupListingsByProperty,
  listingPriceLabel,
  checkGmailStatus,
  updatePropertySpecials,
  applyEmailPreviewMode,
} from "../services/emailService";

import type {
  ClientInfo,
  ListingUnit,
  ListingEmailData,
  GroupedProperty,
  GmailStatus,
} from "../services/emailService";
import {
  buildUnitEditKey,
  isFloorPlanSqftVisible,
  sanitizeFloorPlanModelName,
  sanitizeFloorPlanModelNameForUnit,
  setFloorPlanSqftVisible,
  updateFloorPlanModelName,
} from "./sendListingsDialogUtils";
import { useWalkthroughContext } from "./walkthrough/WalkthroughProvider";
import FakeSendButton from "./walkthrough/FakeSendButton";
import type { SettingsTabKey } from "./settings/settingsTabs";
import { templateService } from "../lib/templateService";
import { resolveTemplateForClient } from "../lib/resolveTemplate";
import {
  listRoleSettings,
  type AdminSettingsRole,
} from "../services/adminSettingsService";

import { revalidateListingDraft } from "./search/revalidateListingDraft";

interface SendListingsDialogProps {
  open: boolean;
  onClose: () => void;
  client: ClientInfo | null;
  selectedListings: ListingUnit[];
  /** Re-evaluate the selected snapshot immediately before a human send. */
  revalidateListings?: () => ListingUnit[];
  selectionMode?: "floorplan" | "unit";
  onSuccess?: () => void;
  onRemoveListing?: (listingId: string) => void;
  onNavigate?: (page: string, clientId?: string, params?: { settingsTab?: SettingsTabKey }) => void;
  isDemo?: boolean;
  isAdmin?: boolean;
  /** Used to fetch the matching role_settings row (admin/agent) so that
   *  admin-locked Send-List defaults apply + admin-disabled toggles hide.
   *  Manager falls back to admin's row (managers have admin-equivalent
   *  send capability per Grant 2026-04-25). */
  userRole?: "admin" | "manager" | "agent" | "demo";
  canToggleEmailImages?: boolean;
  // Persisted compose text restored from sessionStorage on tab return.
  // When provided with non-empty fields, takes precedence over the fresh
  // client-based prefill so the user's in-progress writing isn't lost.
  initialDraft?: {
    subject?: string;
    message?: string;
    recipientEmail?: string;
  };
  // Fired whenever subject/message/recipientEmail changes while the dialog
  // is open, so the parent can persist the latest edits.
  onDraftChange?: (draft: {
    subject: string;
    message: string;
    recipientEmail: string;
  }) => void;
}

const SendListingsDialog: React.FC<SendListingsDialogProps> = ({
  open,
  onClose,
  client,
  selectedListings: initialListings,
  revalidateListings,
  selectionMode = "unit",
  onSuccess,
  isDemo = false,
  isAdmin = false,
  userRole = "agent",
  canToggleEmailImages = false,
  onRemoveListing,
  onNavigate,
  initialDraft,
  onDraftChange,
}) => {
  const revalidateListingsRef = useRef(revalidateListings);
  revalidateListingsRef.current = revalidateListings;
  const theme = useTheme();
  const { isWalkthroughActive, onFakeSend } = useWalkthroughContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const previewScaleContainerRef = useRef<HTMLDivElement>(null);
  const [previewIframeHeight, setPreviewIframeHeight] = useState<number | null>(null);
  const [previewScale, setPreviewScale] = useState<number>(1);
  // Light/Dark toggle for the inline email preview. Forces the email's
  // @media (prefers-color-scheme: dark) rules on regardless of the
  // viewer's OS setting via forceEmailDarkMode().
  const [previewDarkMode, setPreviewDarkMode] = useState(false);
  // Reset iframe height when toggling modes since the content height
  // can shift slightly with different background/text colors. The next
  // onLoad will recompute it.
  useEffect(() => {
    setPreviewIframeHeight(null);
  }, [previewDarkMode]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Admin-only image toggles. Default ON to preserve today's behavior for
  // every locator; admins can force-hide either class per-send.
  const [includePropertyImages, setIncludePropertyImages] = useState(true);
  const [includeFloorPlanImages, setIncludeFloorPlanImages] = useState(true);
  // Hero image tint (matches the identity band's color for header cohesion).
  // Default ON; admins can flip off per-send when the photography should pop.
  const [includeImageOverlays, setIncludeImageOverlays] = useState(true);
  // When on, the tint only paints in dark mode — the light-mode inline
  // tint is suppressed, so the photo pops on white backgrounds. Has no
  // effect when includeImageOverlays is off.
  const [overlaysDarkModeOnly, setOverlaysDarkModeOnly] = useState(false);
  // BETA: when on, floor-plan thumbnail click-through in the email goes to
  // the branded LocateNow /fp/:id page instead of the raw S3 image URL.
  // Default OFF so nothing changes until the operator opts in per-send.
  const [useLocateNowFloorplanPage, setUseLocateNowFloorplanPage] = useState(false);
  // Personal mode (Phase 2 Gmail-deliverability fix). Default ON because
  // every send from THIS dialog is a 1:1 client send. Personal mode tells
  // the backend to skip the RFC 8058 List-Unsubscribe header + skip
  // click/open tracking — the two biggest signals that flip Gmail's
  // classifier to the Promotions tab. CAN-SPAM footer (unsubscribe link
  // + postal address) is preserved in BOTH modes. Toggle OFF to send in
  // "Ultra Marketing mode" (full tracking + RFC 8058 header · the pre-
  // Phase-2 behavior · for explicit marketing campaigns).
  const [personalMode, setPersonalMode] = useState(true);

  // Role-settings hide-and-default state (Admin Settings drawer · PR #72/74).
  // On dialog open, fetch the role_settings row for the current user's role
  // and apply (a) `_default` values to the toggle state hooks above and
  // (b) `_allowed_toggle` flags to gate per-toggle visibility below. Demo
  // users skip the fetch (the whole dialog is gated for them upstream).
  const [roleSettings, setRoleSettings] = useState<{
    sendModeAllowed: boolean;
    propertyImagesAllowed: boolean;
    floorPlanImagesAllowed: boolean;
    linkLNPageAllowed: boolean;
    propertyOpaqueOverlayAllowed: boolean;
    darkModeOnlyAllowed: boolean;
  }>({
    // Default to "all toggles allowed" so the dialog renders correctly while
    // the fetch is in flight. Once the row arrives, the actual flags apply.
    sendModeAllowed: true,
    propertyImagesAllowed: true,
    floorPlanImagesAllowed: true,
    linkLNPageAllowed: true,
    propertyOpaqueOverlayAllowed: true,
    darkModeOnlyAllowed: true,
  });
  const [roleSettingsApplied, setRoleSettingsApplied] = useState(false);

  // Stable idempotency key for the whole life of this open dialog. If the
  // user clicks Send twice (retries, duplicate-submit, etc.) the batch
  // mint replays and returns the same slug map — no orphan rows. Reset
  // when the dialog opens (see useEffect below).
  const idempotencyKeyRef = useRef<string | null>(null);

  // Gmail status
  const [gmailStatus, setGmailStatus] = useState<GmailStatus>({
    connected: false,
    email: null,
    connected_at: null,
    last_used_at: null,
  });
  const [checkingGmail, setCheckingGmail] = useState(true);

  // Listings to send (ungrouped)
  const [listings, setListings] = useState<ListingUnit[]>([]);

  // Grouped properties with editable leasing specials
  const [groupedProperties, setGroupedProperties] = useState<GroupedProperty[]>(
    []
  );

  // Track which specials have been modified
  const [modifiedSpecials, setModifiedSpecials] = useState<Set<string>>(
    new Set()
  );

  // Expanded state for each property card
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(
    new Set()
  );
  const [editingUnitKeys, setEditingUnitKeys] = useState<Set<string>>(new Set());

  // Form state
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");

  // Locator info (loaded from email config)
  const [locatorName, setLocatorName] = useState("");
  const [locatorPhone, setLocatorPhone] = useState("");
  const [locatorEmail, setLocatorEmail] = useState("");
  const [locatorJobTitle, setLocatorJobTitle] = useState("");
  const [locatorPhotoUrl, setLocatorPhotoUrl] = useState("");

  // Preview HTML
  const [previewHtml, setPreviewHtml] = useState("");

  // Scale the 600px-wide email iframe down to fit the mobile viewport.
  // Observes the wrapping Box width and recomputes on resize / orientation.
  useEffect(() => {
    if (!isMobile) {
      setPreviewScale(1);
      return;
    }
    const node = previewScaleContainerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const compute = () => {
      const w = node.clientWidth;
      if (w > 0) setPreviewScale(Math.min(1, w / 600));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isMobile, previewHtml]);

  // Fetch role_settings + apply admin-locked defaults / hide gates on open.
  // Runs once per open (not per render) to avoid clobbering user edits.
  // Demo skips entirely. Manager falls back to admin's row.
  useEffect(() => {
    if (!open || isDemo || roleSettingsApplied) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listRoleSettings();
        if (cancelled) return;
        const targetRole: AdminSettingsRole =
          userRole === "agent" ? "agent" : "admin";
        const row = rows.find((r) => r.role === targetRole);
        if (!row) {
          setRoleSettingsApplied(true);
          return;
        }
        // Apply admin-locked defaults to the toggle state hooks.
        // send_mode_default text → personalMode boolean.
        setPersonalMode(row.send_mode_default === "personal");
        setIncludePropertyImages(row.include_property_images_default);
        setIncludeFloorPlanImages(row.include_floor_plan_images_default);
        setUseLocateNowFloorplanPage(row.link_floor_plans_to_ln_page_default);
        setIncludeImageOverlays(row.property_opaque_overlay_default);
        setOverlaysDarkModeOnly(row.dark_mode_only_default);
        // Cache the allowed_toggle flags for per-toggle visibility gating.
        setRoleSettings({
          sendModeAllowed: row.send_mode_allowed_toggle,
          propertyImagesAllowed: row.include_property_images_allowed_toggle,
          floorPlanImagesAllowed: row.include_floor_plan_images_allowed_toggle,
          linkLNPageAllowed: row.link_floor_plans_to_ln_page_allowed_toggle,
          propertyOpaqueOverlayAllowed:
            row.property_opaque_overlay_allowed_toggle,
          darkModeOnlyAllowed: row.dark_mode_only_allowed_toggle,
        });
        setRoleSettingsApplied(true);
      } catch (err) {
        // Best-effort: failure leaves the existing toggle defaults in place.
        // Don't block sending on a missing/failed role_settings fetch.
        console.warn("role_settings fetch failed:", err);
        setRoleSettingsApplied(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isDemo, userRole, roleSettingsApplied]);

  // Reset the role-settings applied flag when the dialog closes so the next
  // open fetches fresh values.
  useEffect(() => {
    if (!open) setRoleSettingsApplied(false);
  }, [open]);

  // Check Gmail connection on open
  useEffect(() => {
    if (open) {
      setCheckingGmail(true);
      checkGmailStatus().then((status) => {
        setGmailStatus(status);
        setCheckingGmail(false);
      });
      // Mint a fresh idempotency key per client/dialog session. The key is
      // stable across all Send retries within this dialog session, so
      // the backend mint-batch endpoint replays deterministically.
      idempotencyKeyRef.current =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  }, [open, client?.id]);

  // Initialize listings and form on open
  useEffect(() => {
    if (open) {
      setListings([...initialListings]);

      // Group listings by property
      const grouped = groupListingsByProperty(initialListings);
      setGroupedProperties(grouped);

      // Property cards default collapsed — the user can expand any card
      // to edit its leasing special or remove a unit. Keeps the "quick
      // review" frame tight for multi-property selections.
      setExpandedProperties(new Set());

      resetForm();

      // Load locator config
      const config = emailService.getConfig();
      setLocatorName(config.agentName || "");
      setLocatorPhone(config.agentPhone || "");
      setLocatorEmail(config.agentEmail || "");
      setLocatorJobTitle(config.jobTitle || "");
    }
  }, [open, initialListings]);

  // Set recipient email, subject, and personal message.
  //
  // On the FIRST prefill in this open-session we prefer the persisted draft
  // (if the parent supplied one via initialDraft) so a user's in-progress
  // writing survives a tab discard. On subsequent `client` changes within
  // the same open-session, we fall back to fresh client-based prefill —
  // matching the prior behavior when the user explicitly switches clients.
  // `initialDraft` is intentionally NOT a dependency: it's re-broadcast
  // every keystroke by the notify effect below and including it would
  // cause a render loop.
  const initialPrefillDoneRef = useRef(false);
  const prefilledClientIdRef = useRef<string | null>(null);
  const sessionDraftRef = useRef(initialDraft);
  const composeEditRevisionRef = useRef(0);
  const composeWasEditedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initialPrefillDoneRef.current = false;
      prefilledClientIdRef.current = null;
      sessionDraftRef.current = initialDraft;
      composeEditRevisionRef.current = 0;
      composeWasEditedRef.current = false;
      return;
    }
    if (!client) return;

    const isFirstPrefill = !initialPrefillDoneRef.current;
    const isSameClientHydration =
      !isFirstPrefill && prefilledClientIdRef.current === client.id;

    // Snapshot the draft that existed when this open-session began.
    // The parent re-broadcasts the current compose state on every edit, so
    // treating `initialDraft` as live input here makes the subject/body feel
    // locked as the prefill/template logic keeps reapplying.
    if (isFirstPrefill) {
      sessionDraftRef.current = initialDraft;
    } else if (!isSameClientHydration) {
      sessionDraftRef.current = undefined;
      composeWasEditedRef.current = false;
    }
    const sessionDraft = isFirstPrefill ? sessionDraftRef.current : undefined;

    // Literals remain the immediate synchronous prefill so the dialog
    // never shows empty fields on open, and the fallback when no default
    // "listing" email template exists or the lookup fails.
    const literals = {
      recipientEmail: isDemo ? "info@locatenowatl.com" : client.email || "",
      subject: `Apartment Options for ${client.first_name}`,
      message: `Hey ${client.first_name}, here is a quick snapshot of a few places we think you'll like.`,
    };

    if (isFirstPrefill) {
      initialPrefillDoneRef.current = true;
      setRecipientEmail(
        typeof sessionDraft?.recipientEmail === "string"
          ? sessionDraft.recipientEmail
          : literals.recipientEmail
      );
      setEmailSubject(
        typeof sessionDraft?.subject === "string"
          ? sessionDraft.subject
          : literals.subject
      );
      setPersonalMessage(
        typeof sessionDraft?.message === "string"
          ? sessionDraft.message
          : literals.message
      );
    } else if (!isSameClientHydration) {
      // A genuinely different client starts a fresh compose. Replacing a
      // lightweight restored client with the fully hydrated record for the
      // SAME id must not erase the recovered draft or any user edits.
      setRecipientEmail(literals.recipientEmail);
      setEmailSubject(literals.subject);
      setPersonalMessage(literals.message);
    }
    prefilledClientIdRef.current = client.id;

    // Async override: if the user has a default "listing" email template
    // saved, resolve it against the current client + selection and
    // replace subject/message. Respects a tab-discard draft by skipping
    // the override so in-flight edits aren't clobbered on remount.
    let cancelled = false;
    const editRevisionAtLookup = composeEditRevisionRef.current;
    const hasProtectedSessionCopy = Boolean(
      typeof sessionDraftRef.current?.subject === "string" ||
        typeof sessionDraftRef.current?.message === "string"
    );
    (async () => {
      let tmpl;
      try {
        tmpl = await templateService.getDefaultTemplate("email", "listing");
      } catch (err) {
        console.warn(
          "default email template lookup failed; using literals:",
          err,
        );
        return;
      }
      if (cancelled || !tmpl) return;
      if (hasProtectedSessionCopy) return;
      if (composeWasEditedRef.current) return;
      if (composeEditRevisionRef.current !== editRevisionAtLookup) return;

      // Pull fresh locator config directly — the sibling useEffect that
      // populates locator state hasn't necessarily committed by the time
      // this block runs, and getConfig is a synchronous read.
      const config = emailService.getConfig();
      const resolved = resolveTemplateForClient({
        template: tmpl,
        client,
        agent: {
          full_name: config.agentName || null,
          email: config.agentEmail || null,
          phone: config.agentPhone || null,
        },
        selectedProperties: groupListingsByProperty(initialListings).map((p) => ({
          property_name: p.property_name,
        })),
      });

      if (
        cancelled ||
        composeWasEditedRef.current ||
        composeEditRevisionRef.current !== editRevisionAtLookup
      ) return;
      if (resolved.subject) setEmailSubject(resolved.subject);
      if (resolved.body) setPersonalMessage(resolved.body);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, client, isDemo, initialListings]);

  // Notify parent of any edit so it can persist to the session draft.
  useEffect(() => {
    if (!open || !onDraftChange) return;
    onDraftChange({
      subject: emailSubject,
      message: personalMessage,
      recipientEmail,
    });
  }, [open, emailSubject, personalMessage, recipientEmail, onDraftChange]);

  // Fetch locator's avatar_url from profiles table
  useEffect(() => {
    const fetchLocatorPhoto = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("avatar_url")
            .eq("id", user.id)
            .single();

          if (profile?.avatar_url) {
            setLocatorPhotoUrl(profile.avatar_url);
          }
        }
      } catch (err) {
        console.error("Error fetching locator photo:", err);
      }
    };

    if (open) {
      fetchLocatorPhoto();
    }
  }, [open]);

  // Update preview when showing preview
  useEffect(() => {
    if (showPreview && client && groupedProperties.length > 0) {
      updatePreview();
    }
  }, [
    showPreview,
    client,
    groupedProperties,
    locatorName,
    locatorPhone,
    locatorEmail,
    locatorJobTitle,
    locatorPhotoUrl,
    personalMessage,
    includePropertyImages,
    includeFloorPlanImages,
    includeImageOverlays,
    overlaysDarkModeOnly,
    useLocateNowFloorplanPage,
  ]);

  const resetForm = () => {
    setError(null);
    setSuccess(false);
    setShowPreview(false);
    setModifiedSpecials(new Set());
    setEditingUnitKeys(new Set());
  };

  const updatePreview = () => {
    if (!client || groupedProperties.length === 0) return;

    const data: ListingEmailData = {
      client,
      listings,
      groupedProperties,
      locatorName,
      locatorPhone,
      locatorEmail,
      locatorJobTitle,
      locatorPhotoUrl,
      customMessage: personalMessage,
      selectionMode,
      includePropertyImages,
      includeFloorPlanImages,
      includeImageOverlays,
      overlaysDarkModeOnly,
      useLocateNowFloorplanPage,
    };

    setPreviewHtml(generateListingEmailHtml(data));
  };

  // Toggle property expansion
  const togglePropertyExpanded = (propertyId: string) => {
    setExpandedProperties((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  const toggleUnitEditing = (propertyId: string, unitId: string) => {
    const key = buildUnitEditKey(propertyId, unitId);
    setEditingUnitKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleUnitModelNameChange = (
    propertyId: string,
    unitId: string,
    modelName: string
  ) => {
    setGroupedProperties((prev) =>
      updateFloorPlanModelName(prev, propertyId, unitId, modelName)
    );
  };

  const handleSanitizeUnitModelName = (propertyId: string, unitId: string) => {
    setGroupedProperties((prev) =>
      sanitizeFloorPlanModelNameForUnit(prev, propertyId, unitId)
    );
  };

  const handleUnitSqftVisibleChange = (
    propertyId: string,
    unitId: string,
    visible: boolean
  ) => {
    setGroupedProperties((prev) =>
      setFloorPlanSqftVisible(prev, propertyId, unitId, visible)
    );
  };

  // Update leasing special for a property
  const handleLeasingSpecialChange = (propertyId: string, value: string) => {
    setGroupedProperties((prev) =>
      prev.map((p) =>
        p.property_id === propertyId ? { ...p, leasing_special: value } : p
      )
    );
    setModifiedSpecials((prev) => new Set(prev).add(propertyId));
  };

  // Save leasing special to database
  const handleSaveLeasingSpecial = async (propertyId: string) => {
    const property = groupedProperties.find(
      (p) => p.property_id === propertyId
    );
    if (!property) return;

    const result = await updatePropertySpecials(
      propertyId,
      property.leasing_special || ""
    );
    if (result.success) {
      setModifiedSpecials((prev) => {
        const newSet = new Set(prev);
        newSet.delete(propertyId);
        return newSet;
      });
    } else {
      setError(result.error || "Failed to save leasing special");
    }
  };

  // Remove a unit from a property
  const handleRemoveUnit = (propertyId: string, unitId: string) => {
    setGroupedProperties((prev) => {
      const updated = prev.map((p) => {
        if (p.property_id === propertyId) {
          return {
            ...p,
            units: p.units.filter((u) => u.id !== unitId),
          };
        }
        return p;
      });
      return updated.filter((p) => p.units.length > 0);
    });

    setEditingUnitKeys((prev) => {
      const next = new Set(prev);
      next.delete(buildUnitEditKey(propertyId, unitId));
      return next;
    });

    setListings((prev) => prev.filter((l) => l.id !== unitId));
    onRemoveListing?.(unitId);
  };

  // Remove entire property
  const handleRemoveProperty = (propertyId: string) => {
    const property = groupedProperties.find(
      (p) => p.property_id === propertyId
    );
    if (!property) return;

    property.units.forEach((unit) => {
      onRemoveListing?.(unit.id);
    });

    setGroupedProperties((prev) =>
      prev.filter((p) => p.property_id !== propertyId)
    );

    setEditingUnitKeys((prev) => {
      const next = new Set(prev);
      property.units.forEach((unit) =>
        next.delete(buildUnitEditKey(propertyId, unit.id))
      );
      return next;
    });

    setListings((prev) => prev.filter((l) => l.property_id !== propertyId));
  };

  // Count total units
  const totalUnits = useMemo(
    () => groupedProperties.reduce((sum, p) => sum + p.units.length, 0),
    [groupedProperties]
  );

  // Save sent list to client's sent_list column
  const saveSentListToClient = async (
    emailHtml: string,
    trackingId?: string,
    floorPlanSlugs?: Record<string, string>
  ) => {
    if (!client) return;

    try {
      // First, get the current sent_list from the client
      const { data: currentClient, error: fetchError } = await supabase
        .from("clients")
        .select("sent_list")
        .eq("id", client.id)
        .single();

      if (fetchError) {
        console.error("Error fetching client sent_list:", fetchError);
        return;
      }

      // Build the sent list entry
      const sentEntry: Record<string, any> = {
        sent_at: new Date().toISOString(),
        recipient_email: recipientEmail,
        subject: emailSubject,
        personal_message: personalMessage,
        selection_mode: selectionMode,
        ...(trackingId && { tracking_id: trackingId }),
        locator: {
          name: locatorName,
          phone: locatorPhone,
          email: locatorEmail,
          job_title: locatorJobTitle,
          photo_url: locatorPhotoUrl,
        },
        properties: groupedProperties.map((property) => ({
          property_id: property.property_id,
          property_name: property.property_name,
          neighborhood: property.neighborhood,
          photo_url: property.photo_url,
          property_url: property.property_url,
          leasing_special: property.leasing_special,
          units: property.units.map((unit) => ({
            id: unit.id,
            model_name: unit.model_name,
            bedrooms: unit.bedrooms,
            bathrooms: unit.bathrooms,
            square_feet: unit.square_feet,
            hide_square_feet_in_email: unit.hide_square_feet_in_email,
            current_price: unit.current_price,
            status: unit.status,
            available_date: unit.available_date,
            floorplan_url: unit.floorplan_url,
            floor_plan_id: unit.floor_plan_id,
            floorplan_slug: unit.floor_plan_id
              ? floorPlanSlugs?.[unit.floor_plan_id] || null
              : null,
          })),
        })),
        email_html: emailHtml,
      };

      // Append to existing sent_list or create new array
      const existingSentList = currentClient?.sent_list || [];
      const updatedSentList = [...existingSentList, sentEntry];

      // Update the client's sent_list
      const { error: updateError } = await supabase
        .from("clients")
        .update({ sent_list: updatedSentList })
        .eq("id", client.id);

      if (updateError) {
        console.error("Error saving sent_list:", updateError);
      } else {
        console.log("Sent list saved successfully");
      }
    } catch (err) {
      console.error("Error saving sent list to client:", err);
    }
  };

  // Fence this particular reviewed draft, including Close/reopen and client
  // changes while edit saves or link preparation are awaited. The ref lock is
  // synchronous, so same-render double clicks cannot start two sends.
  const sendSessionRef = useRef({ open, clientId: client?.id, generation: 0 });
  const sendAttemptRef = useRef<symbol | null>(null);
  if (sendSessionRef.current.open !== open || sendSessionRef.current.clientId !== client?.id) {
    sendSessionRef.current = { open, clientId: client?.id, generation: sendSessionRef.current.generation + 1 };
    sendAttemptRef.current = null;
  }
  const sendDraftRef = useRef("");
  sendDraftRef.current = JSON.stringify({
    client, recipientEmail, emailSubject, personalMessage, listings, groupedProperties,
    locatorName, locatorPhone, locatorEmail, locatorJobTitle, locatorPhotoUrl,
    selectionMode, includePropertyImages, includeFloorPlanImages, includeImageOverlays,
    overlaysDarkModeOnly, useLocateNowFloorplanPage, personalMode,
  });
  useEffect(() => {
    setLoading(false);
    return () => {
      sendSessionRef.current.generation += 1;
      sendAttemptRef.current = null;
    };
  }, [open, client?.id]);
  const closeSendDialog = () => {
    sendSessionRef.current.generation += 1;
    sendAttemptRef.current = null;
    onClose();
  };

  const confirmCurrentPricing = (): boolean => {
    if (!revalidateListingsRef.current) return true;
    try {
      const next = revalidateListingDraft(listings, groupedProperties, revalidateListingsRef.current());
      if (!next.changed) return true;
      setListings(next.listings);
      setGroupedProperties(next.groupedProperties);
      setError("Availability or pricing changed. Review the updated listings, then click Send again.");
    } catch {
      setError("Couldn't verify the selected pricing. Close this draft and reopen it from Search before sending.");
    }
    return false;
  };

  const handleSend = async () => {
    if (!client || !open || sendAttemptRef.current) return;
    if (!recipientEmail) {
      setError("Please enter a recipient email address");
      return;
    }
    if (!emailSubject.trim()) {
      setError("Please enter an email subject");
      return;
    }
    if (groupedProperties.length === 0) {
      setError("Please select at least one listing to send");
      return;
    }
    if (!isDemo && !gmailStatus.connected) {
      setError("Please connect your Gmail account in Settings to send emails");
      return;
    }

    // Revalidation never sends automatically: changed fields require review
    // and a second explicit click, before even saving edits or minting links.
    if (!confirmCurrentPricing()) return;
    const attempt = Symbol("reviewed-listing-send");
    sendAttemptRef.current = attempt;
    const generation = sendSessionRef.current.generation;
    const reviewedDraft = sendDraftRef.current;
    const sameSession = () => sendSessionRef.current.open &&
      sendSessionRef.current.clientId === client.id && sendSessionRef.current.generation === generation;
    const currentAttempt = () => sameSession() && sendAttemptRef.current === attempt;
    const stillReviewed = () => {
      if (!currentAttempt()) return false;
      if (sendDraftRef.current === reviewedDraft) return true;
      setError("The draft changed while preparing the email. Review it and click Send again.");
      return false;
    };
    setLoading(true);
    setError(null);

    try {
      // Save any modified specials before sending
      const modifiedIds = Array.from(modifiedSpecials);
      for (let i = 0; i < modifiedIds.length; i++) {
        await handleSaveLeasingSpecial(modifiedIds[i]);
        if (!stillReviewed()) return;
      }

      // When the LocateNow page toggle is ON, mint a short slug per
      // floor plan so the email links to /fp/:slug instead of exposing
      // raw DB UUIDs. Minting happens in a single batch call.
      //
      // Failure policy (per security review): if the mint fails for any
      // reason, BLOCK the send rather than silently falling back to the
      // raw UUID URL — silent fallback would quietly re-introduce the
      // very leak we're fixing. The operator can retry the send; the
      // idempotency key makes the retry safe (no orphan rows).
      let floorPlanSlugs: Record<string, string> | undefined;
      if (useLocateNowFloorplanPage) {
        const uniqueFpIds = Array.from(
          new Set(
            listings
              .map((l) => l.floor_plan_id)
              .filter((id): id is string => !!id),
          ),
        );
        if (uniqueFpIds.length === 0) {
          setError(
            "Can't generate LocateNow links for this send — none of the selected listings have a floor-plan id yet. Uncheck the toggle or pick listings with complete data.",
          );
          setLoading(false);
          return;
        }
        if (!idempotencyKeyRef.current) {
          idempotencyKeyRef.current =
            typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        try {
          const resp = await fetchWithAuth<{ slugs: Record<string, string> }>(
            "/floorplan-tokens/batch",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                idempotency_key: idempotencyKeyRef.current,
                floor_plan_ids: uniqueFpIds,
                client_id: client.id,
              }),
            },
          );
          if (!stillReviewed()) return;
          floorPlanSlugs = resp.slugs;
        } catch (mintErr: any) {
          if (!currentAttempt()) return;
          // Log server-side detail for us; show a generic, actionable
          // message to the operator. Do NOT fall back to raw UUIDs.
          console.error("floorplan_token_mint_failed", mintErr);
          setError(
            "Couldn't generate LocateNow share links for this send. Try again in a moment, or uncheck the toggle to send with standard links.",
          );
          setLoading(false);
          return;
        }
      }

      // Edits/link preparation can await network work. Recheck the latest
      // selected target before generating either MIME part or sending.
      if (!stillReviewed() || !confirmCurrentPricing()) return;

      const data: ListingEmailData = {
        client,
        listings,
        groupedProperties,
        locatorName,
        locatorPhone,
        locatorEmail,
        locatorJobTitle,
        locatorPhotoUrl,
        customMessage: personalMessage,
        selectionMode,
        includePropertyImages,
        includeFloorPlanImages,
        includeImageOverlays,
        overlaysDarkModeOnly,
        useLocateNowFloorplanPage,
        floorPlanSlugs,
      };

      // Generate the email HTML to save
      const emailHtml = generateListingEmailHtml(data);

      const result = await emailService.sendListings(
        data,
        recipientEmail,
        emailSubject,
        personalMode ? "personal" : "campaign",
      );

      if (result.success) {
        // Save the sent list to the client's sent_list column
        await saveSentListToClient(emailHtml, result.trackingId, floorPlanSlugs);

        // A provider request already submitted cannot be unsent. Keep its
        // original sent-list receipt, but never update a newer/closed session.
        if (!currentAttempt()) return;
        setSuccess(true);
        onSuccess?.();
        setTimeout(() => {
          if (sameSession()) closeSendDialog();
        }, 2000);
      } else if (currentAttempt()) {
        setError(result.error || "Failed to send listings");
      }
    } catch (err: any) {
      if (currentAttempt()) setError(err.message || "An error occurred");
    } finally {
      if (sendAttemptRef.current === attempt) {
        sendAttemptRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleGoToSettings = () => {
    closeSendDialog();
    onNavigate?.("settings", undefined, { settingsTab: "integrations" });
  };

  if (!client) return null;

  return (
    <Dialog
      open={open}
      onClose={closeSendDialog}
      maxWidth="md"
      fullWidth
      data-tour="send-listings-dialog"
      fullScreen={isMobile}
      PaperProps={{
        sx: isMobile
          ? undefined
          : { minHeight: "70vh", maxHeight: "90vh" },
      }}
    >
      <DialogTitle sx={{ py: 1.25, pr: 1.25 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32,
              }}
            >
              <EmailIcon fontSize="small" />
            </Avatar>
            <Box sx={{ lineHeight: 1.2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                Send Listings
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2, display: "block" }}
              >
                {totalUnits} {selectionMode === "floorplan" ? "floor plan" : "unit"}
                {totalUnits !== 1 ? "s" : ""} from{" "}
                {groupedProperties.length} propert
                {groupedProperties.length !== 1 ? "ies" : "y"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeSendDialog} size="small" aria-label="Close send dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1.5 }}>
        {/* Gmail Connection Warning (hidden for demo) */}
        {!isDemo && !checkingGmail && !gmailStatus.connected && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleGoToSettings}>
                Connect Gmail
              </Button>
            }
          >
            Gmail not connected. Connect your account to send emails.
          </Alert>
        )}

        {/* "Sending from <email>" moved into the DialogActions footer for a
            cleaner content area — see the flex-grow Box in DialogActions. */}

        {/* Demo mode info */}
        {isDemo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Demo mode — use Preview to see how listing emails look. Sending is disabled.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Email sent successfully to {recipientEmail}!
          </Alert>
        )}

        {!showPreview ? (
          <Grid container spacing={2.5}>
            {/* Client & Email Info Section */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Grid container spacing={1.5}>
                  {/* Recipient */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Recipient Email"
                      value={recipientEmail}
                      onChange={(e) => {
                        composeEditRevisionRef.current += 1;
                        composeWasEditedRef.current = true;
                        setRecipientEmail(e.target.value);
                      }}
                      type="email"
                      required
                    />
                  </Grid>

                  {/* Subject */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email Subject"
                      value={emailSubject}
                      onChange={(e) => {
                        composeEditRevisionRef.current += 1;
                        composeWasEditedRef.current = true;
                        setEmailSubject(e.target.value);
                      }}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SubjectIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Personal Message */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Personal Message"
                      value={personalMessage}
                      onChange={(e) => {
                        composeEditRevisionRef.current += 1;
                        composeWasEditedRef.current = true;
                        setPersonalMessage(e.target.value);
                      }}
                      multiline
                      rows={2}
                      placeholder="Add a personal message to your client..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{ alignSelf: "flex-start", mt: 1 }}
                          >
                            <MessageIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Personal mode toggle — Phase 2 Gmail-deliverability fix.
                Default ON · turning OFF sends in Ultra Marketing mode
                (full tracking + RFC 8058 header · the pre-Phase-2 behavior).
                CAN-SPAM footer preserved both. Hidden entirely when admin
                has locked send_mode for this role (PR #83 + cleanup) —
                the system silently uses the locked default and the
                operator doesn't see vertical chrome they can't act on. */}
            {roleSettings.sendModeAllowed && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personalMode}
                        onChange={(e) => setPersonalMode(e.target.checked)}
                        data-tour="personal-mode-toggle"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Personal mode
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", lineHeight: 1.3 }}
                        >
                          {personalMode
                            ? "Better inbox placement · no tracking pixel · no click rewrites · unsubscribe footer kept"
                            : "Ultra Marketing mode · full tracking + RFC 8058 unsubscribe header (more likely Promotions tab)"}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0 }}
                  />
                </Paper>
              </Grid>
            )}

            {/* Image toggles — only render the wrapper Paper when the user
                has the capability AND at least one toggle is allowed by
                Admin Settings. Otherwise the header card sits empty. */}
            {canToggleEmailImages &&
              (roleSettings.propertyImagesAllowed ||
                roleSettings.floorPlanImagesAllowed ||
                roleSettings.linkLNPageAllowed ||
                roleSettings.propertyOpaqueOverlayAllowed ||
                roleSettings.darkModeOnlyAllowed) && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                    borderColor: alpha(theme.palette.warning.main, 0.3),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    Admin · Email Image Options
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 3 }}>
                    {roleSettings.propertyImagesAllowed && (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={includePropertyImages}
                            onChange={(e) => setIncludePropertyImages(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Include property images
                          </Typography>
                        }
                      />
                    )}
                    {roleSettings.floorPlanImagesAllowed && (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={includeFloorPlanImages}
                            onChange={(e) => setIncludeFloorPlanImages(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Include floor plan images
                          </Typography>
                        }
                      />
                    )}
                    {roleSettings.linkLNPageAllowed && (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={useLocateNowFloorplanPage}
                            onChange={(e) => setUseLocateNowFloorplanPage(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Link floor plans to LocateNow page{" "}
                            <Box component="span" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                              (beta — live availability)
                            </Box>
                          </Typography>
                        }
                      />
                    )}
                    {roleSettings.propertyOpaqueOverlayAllowed && (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={includeImageOverlays && !overlaysDarkModeOnly}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Activating "both modes" clears dark-only so
                                // the two switches stay mutually exclusive.
                                setIncludeImageOverlays(true);
                                setOverlaysDarkModeOnly(false);
                              } else {
                                setIncludeImageOverlays(false);
                                setOverlaysDarkModeOnly(false);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Property opaque overlay
                          </Typography>
                        }
                      />
                    )}
                    {roleSettings.darkModeOnlyAllowed && (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={includeImageOverlays && overlaysDarkModeOnly}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Activating "dark only" implies overlays are
                                // on (scoped to dark mode), and flips off the
                                // "both modes" switch.
                                setIncludeImageOverlays(true);
                                setOverlaysDarkModeOnly(true);
                              } else {
                                setIncludeImageOverlays(false);
                                setOverlaysDarkModeOnly(false);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Dark mode only
                          </Typography>
                        }
                      />
                    )}
                  </Stack>
                </Paper>
              </Grid>
            )}

            {/* Selected Properties */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Selection
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {groupedProperties.length} propert{groupedProperties.length !== 1 ? "ies" : "y"} · {totalUnits} {selectionMode === "floorplan" ? "floor plan" : "unit"}{totalUnits !== 1 ? "s" : ""}
                </Typography>
              </Stack>

              {groupedProperties.length === 0 ? (
                // This branch is only reachable while a persisted open-state
                // is being restored (the Email FAB requires selections, so
                // the user can't normally open this dialog with zero
                // listings). Render a skeleton that matches the real list
                // shape so there's no layout jump when listings arrive.
                <Box sx={{ maxHeight: 280, overflow: "hidden", pr: 0.5 }}>
                  {[0, 1].map((i) => (
                    <Paper
                      key={i}
                      variant="outlined"
                      sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden", p: 1.5 }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Skeleton variant="rounded" width={48} height={48} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" height={22} />
                          <Skeleton variant="text" width="40%" height={18} />
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1.25, display: "flex", gap: 1 }}>
                        <Skeleton variant="rounded" width={80} height={28} />
                        <Skeleton variant="rounded" width={80} height={28} />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box sx={{ maxHeight: 280, overflow: "auto", pr: 0.5 }}>
                  {groupedProperties.map((property) => (
                    <Paper
                      key={property.property_id}
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      {/* Property Header — Unifi identity band: photo + name + neighborhood chip + unit count */}
                      <Box
                        sx={{
                          p: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          bgcolor: alpha(theme.palette.grey[500], 0.04),
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          togglePropertyExpanded(property.property_id)
                        }
                      >
                        <Avatar
                          variant="rounded"
                          src={property.photo_url}
                          sx={{ width: 40, height: 40, flexShrink: 0 }}
                        >
                          <HomeIcon fontSize="small" />
                        </Avatar>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {property.property_name}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{ mt: 0.25, flexWrap: "wrap", rowGap: 0.25 }}
                          >
                            {property.neighborhood && (
                              <Chip
                                label={property.neighborhood}
                                size="small"
                                sx={{
                                  fontSize: 10,
                                  height: 18,
                                  fontWeight: 600,
                                  borderRadius: 1.25,
                                }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {property.units.length}{" "}
                              {selectionMode === "floorplan" ? "floor plan" : "unit"}
                              {property.units.length !== 1 ? "s" : ""}
                            </Typography>
                          </Stack>
                        </Box>

                        <Tooltip title="Remove property">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveProperty(property.property_id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <IconButton size="small">
                          {expandedProperties.has(property.property_id) ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </Box>

                      {/* Expanded Content */}
                      <Collapse
                        in={expandedProperties.has(property.property_id)}
                      >
                        <Box sx={{ p: 1.5, pt: 0 }}>
                          {/* Leasing Special (Editable) */}
                          <TextField
                            fullWidth
                            size="small"
                            label="Leasing Special"
                            placeholder="e.g., 8 Weeks Free • Move-In By 11/30"
                            value={property.leasing_special || ""}
                            onChange={(e) =>
                              handleLeasingSpecialChange(
                                property.property_id,
                                e.target.value
                              )
                            }
                            sx={{ mt: 1.5, mb: 1 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EditIcon fontSize="small" color="action" />
                                </InputAdornment>
                              ),
                              endAdornment: modifiedSpecials.has(
                                property.property_id
                              ) && (
                                <InputAdornment position="end">
                                  <Tooltip title="Save to database">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleSaveLeasingSpecial(
                                          property.property_id
                                        )
                                      }
                                      color="primary"
                                    >
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />

                          {/* Units — flex rows (no ListItem/SecondaryAction so
                              price and action icons never overlap on mobile) */}
                          <Box>
                            {property.units.map((unit) => {
                              const unitKey = buildUnitEditKey(
                                property.property_id,
                                unit.id
                              );
                              const isUnitEditing = editingUnitKeys.has(unitKey);
                              const modelName = unit.model_name || "Unit";
                              const sanitizedModelName = sanitizeFloorPlanModelName(
                                unit.model_name
                              );
                              const showSqftInEmail = isFloorPlanSqftVisible(
                                unit.square_feet,
                                unit.hide_square_feet_in_email
                              );
                              const canSanitize =
                                (unit.model_name || "").trim() !==
                                sanitizedModelName;

                              return (
                                <React.Fragment key={unit.id}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          theme.palette.action.hover,
                                          0.5
                                        ),
                                      },
                                    }}
                                  >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        fontSize={13}
                                        component="div"
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          columnGap: 0.75,
                                          rowGap: 0.1,
                                          alignItems: "baseline",
                                        }}
                                      >
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                          {modelName}
                                        </Box>
                                        <Box
                                          component="span"
                                          sx={{ color: "text.secondary" }}
                                        >
                                          {unit.bedrooms}BR/{unit.bathrooms}BA
                                          {showSqftInEmail && unit.square_feet
                                            ? ` · ${unit.square_feet.toLocaleString()} sqft`
                                            : ""}
                                        </Box>
                                      </Typography>
                                      {!showSqftInEmail && unit.square_feet ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: "block", lineHeight: 1.2 }}
                                        >
                                          sqft hidden in email
                                        </Typography>
                                      ) : null}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      fontSize={13}
                                      sx={{
                                        fontWeight: 600,
                                        fontVariantNumeric: "tabular-nums",
                                        flexShrink: 0,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {listingPriceLabel(unit) ? `${listingPriceLabel(unit)}/mo` : "Confirm pricing"}
                                    </Typography>
                                    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                                      {selectionMode === "floorplan" && (
                                        <Tooltip
                                          title={
                                            isUnitEditing
                                              ? "Done editing"
                                              : "Edit floor plan"
                                          }
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              toggleUnitEditing(
                                                property.property_id,
                                                unit.id
                                              )
                                            }
                                            color={
                                              isUnitEditing ? "primary" : "default"
                                            }
                                          >
                                            {isUnitEditing ? (
                                              <CheckIcon fontSize="small" />
                                            ) : (
                                              <EditIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip
                                        title={
                                          selectionMode === "floorplan"
                                            ? "Remove floor plan"
                                            : "Remove unit"
                                        }
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleRemoveUnit(
                                              property.property_id,
                                              unit.id
                                            )
                                          }
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </Box>

                                  {selectionMode === "floorplan" &&
                                    isUnitEditing && (
                                      <Box sx={{ px: 1, pb: 1, ml: 5 }}>
                                        <Stack spacing={1}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Floor plan name"
                                            value={unit.model_name || ""}
                                            onChange={(e) =>
                                              handleUnitModelNameChange(
                                                property.property_id,
                                                unit.id,
                                                e.target.value
                                              )
                                            }
                                          />
                                          <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={1}
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                          >
                                            <Button
                                              size="small"
                                              variant="text"
                                              startIcon={
                                                <AutoFixHighIcon fontSize="small" />
                                              }
                                              onClick={() =>
                                                handleSanitizeUnitModelName(
                                                  property.property_id,
                                                  unit.id
                                                )
                                              }
                                              disabled={!canSanitize}
                                            >
                                              Clean Floor Plan
                                            </Button>
                                            <FormControlLabel
                                              sx={{ ml: { xs: 0, sm: 0.5 } }}
                                              control={
                                                <Switch
                                                  size="small"
                                                  checked={showSqftInEmail}
                                                  onChange={(e) =>
                                                    handleUnitSqftVisibleChange(
                                                      property.property_id,
                                                      unit.id,
                                                      e.target.checked
                                                    )
                                                  }
                                                  disabled={!unit.square_feet}
                                                />
                                              }
                                              label={
                                                <Typography variant="caption">
                                                  Show sqft in email
                                                </Typography>
                                              }
                                            />
                                          </Stack>
                                        </Stack>
                                      </Box>
                                    )}
                                </React.Fragment>
                              );
                            })}
                          </Box>
                        </Box>
                      </Collapse>
                    </Paper>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Your Contact Info */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Your Contact Info
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={locatorName}
                    onChange={(e) => setLocatorName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={locatorJobTitle}
                    onChange={(e) => setLocatorJobTitle(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone"
                    value={locatorPhone}
                    onChange={(e) => setLocatorPhone(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    value={locatorEmail}
                    onChange={(e) => setLocatorEmail(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          /* Preview Mode */
          <Box>
            {groupedProperties.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 2,
                }}
              >
                <Typography color="text.secondary">
                  Select listings to see preview
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Light/Dark toggle for the preview iframe. Sits above
                    the email card, scoped to the preview only — does not
                    affect the dialog chrome or the email that ships. */}
                <Box
                  sx={{
                    maxWidth: 640,
                    mx: "auto",
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    pb: 1,
                  }}
                >
                  <ToggleButtonGroup
                    value={previewDarkMode ? "dark" : "light"}
                    exclusive
                    size="small"
                    onChange={(_e, value) => {
                      // value is null when the user clicks the
                      // already-selected button — keep current mode.
                      if (value !== null)
                        setPreviewDarkMode(value === "dark");
                    }}
                    aria-label="Preview color scheme"
                  >
                    <ToggleButton value="light" aria-label="Light mode preview">
                      <LightModeIcon fontSize="small" sx={{ mr: 0.5 }} /> Light
                    </ToggleButton>
                    <ToggleButton value="dark" aria-label="Dark mode preview">
                      <DarkModeIcon fontSize="small" sx={{ mr: 0.5 }} /> Dark
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 0,
                    borderRadius: 2,
                    overflow: "hidden",
                    maxWidth: 640,
                    mx: "auto",
                    width: "100%",
                    backgroundColor: previewDarkMode ? "#18181b" : "transparent",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      position: "relative",
                      // On mobile the iframe is 600px wide and transformed
                      // with scale(); reserve the scaled height so content
                      // after it isn't pushed down by the un-transformed box.
                      height:
                        isMobile && previewIframeHeight
                          ? `${Math.round(previewIframeHeight * previewScale + 4)}px`
                          : undefined,
                    }}
                    ref={previewScaleContainerRef}
                  >
                    <iframe
                      ref={previewIframeRef}
                      srcDoc={applyEmailPreviewMode(
                        previewHtml,
                        previewDarkMode ? "dark" : "light"
                      )}
                      onLoad={() => {
                        const doc = previewIframeRef.current?.contentDocument;
                        if (doc?.body) {
                          setPreviewIframeHeight(doc.body.scrollHeight + 4);
                        }
                      }}
                      style={{
                        width: isMobile ? "600px" : "100%",
                        height: previewIframeHeight ? `${previewIframeHeight}px` : "600px",
                        border: "none",
                        display: "block",
                        transform: isMobile
                          ? `scale(${previewScale})`
                          : undefined,
                        transformOrigin: "top left",
                      }}
                      title="Email Preview"
                    />
                  </Box>
                </Paper>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, gap: 1, borderTop: 1, borderColor: "divider" }}>
        {/* Left-aligned "Sending from" — replaces the top-of-dialog Alert.
            Hidden on xs so the action buttons stay clean on narrow screens. */}
        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
          {!isDemo && gmailStatus.connected && gmailStatus.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />
              Sending from&nbsp;<strong>{gmailStatus.email}</strong>
            </Typography>
          )}
        </Box>
        <Button size="small" onClick={closeSendDialog} disabled={loading}>
          Cancel
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowPreview(!showPreview)}
          startIcon={<PreviewIcon fontSize="small" />}
          data-tour="email-preview-btn"
          disabled={groupedProperties.length === 0}
        >
          {showPreview ? "Edit" : "Preview"}
        </Button>

        {!isDemo ? (
          <Button
            size="small"
            variant="contained"
            onClick={handleSend}
            disabled={
              loading ||
              !recipientEmail ||
              !emailSubject.trim() ||
              groupedProperties.length === 0 ||
              success ||
              !gmailStatus.connected
            }
            startIcon={loading ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
          >
            {loading ? "Sending..." : "Send"}
          </Button>
        ) : isWalkthroughActive ? (
          <FakeSendButton onClick={onFakeSend} />
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default SendListingsDialog;
